/**
 * Migration Adaptor
 *
 * 用于迁移老版本 capability 调用代码，将新版本的返回结果包装为老版本的 Response 结构
 */

/**
 * 失败输出结构
 */
export interface FailureOutput {
  response: {
    status: {
      /** 协议层返回码（如 HTTP 状态码） */
      protocolStatusCode: string;
      /** 业务应用状态码 */
      appStatusCode: string;
    };
    /** 返回体（JSON 字符串形式） */
    body: string;
  };
}

/**
 * 老版本 capability 调用的 Response 结构
 *
 * 注意：output 使用 any 类型以简化迁移代码，避免类型断言
 */
export interface MigrationResponse {
  /** 当 code=0 时表示执行成功，当 code!=0 时表示执行失败 */
  code: number;
  data?: {
    /** 执行成功时的输出结果（any 类型，兼容迁移场景） */
    output?: any;
    /** 执行结果，'success' 或 'error' */
    outcome?: 'success' | 'error';
    /** 执行失败时的输出结果 */
    failureOutput?: FailureOutput;
  };
  /** 发生错误时的错误信息 */
  message?: string;
}

/**
 * 迁移适配器
 *
 * 将 CapabilityService.load().call() 的返回结果包装为老版本的 Response 结构
 *
 * @example
 * // 老代码
 * const result = await generateTaskDescription(params);
 *
 * // 新代码
 * const result = await migrationAdaptor(
 *   this.capabilityService.load('ai_generate_task_description').call('run', params)
 * );
 *
 * // result 结构
 * // {
 * //   code: 0,
 * //   data: {
 * //     output: { ... },  // 能力执行结果
 * //     outcome: 'success'
 * //   }
 * // }
 */
export async function migrationAdaptor(
  promise: Promise<unknown>,
): Promise<MigrationResponse> {
  try {
    const result = await promise;

    return {
      code: 0,
      data: {
        output: result,
        outcome: 'success',
      },
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const appStatusCode = error instanceof Error && 'code' in error ? String(error.code) : 'EXECUTION_ERROR';

    return {
      code: -1,
      data: {
        outcome: 'error',
        failureOutput: {
          response: {
            status: {
              protocolStatusCode: '500',
              appStatusCode,
            },
            body: JSON.stringify({
              error: errorMessage,
              stack: error instanceof Error ? error.stack : undefined,
            }),
          },
        },
      },
      message: errorMessage,
    };
  }
}
